import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as OIDC from "openid-client";
import config from "../config.js";
import info from "../info.json"
import { ButtonOk } from "../Components/Buttons.js";
import { Page } from "../Components/page.js";
import { useProfiles } from "../lib/profiles.js";

function twinHostFromUrl(twinUrl) {
  if (!twinUrl) return null;
  let hostPattern = /^https?\:\/\/(?<host>[a-f0-9]{32}\.[^\.]+\.biz\.todaq\.net)/i;
  // e.g.: https://4147cd7ee79b91309ce6f03fb0695b80.acme.biz.todaq.net
  if (info.env === "STAGING") {
    hostPattern = /^https?\:\/\/(?<host>[a-f0-9]{32}\.[^\.]+\.biz-staging\.todaq\.net)/i;
    // e.g.: https://4147cd7ee79b91309ce6f03fb0695b80.acme.biz-staging.todaq.net
  } else if (info.env === "DEV") {
    hostPattern = /^https?\:\/\/(?<host>[^\.]+\.[^\.]+\.(biz|biz-staging)\.todaq\.net)/i
    // e.g.: http://alice.todaq-test.biz.todaq.net:8187
    // e.g.: http://bob.todaq-test.biz.todaq.net
    // e.g.: http://charles.ttt.biz-staging.todaq.net:8187
  }

  return twinUrl.match(hostPattern)?.groups?.host;
}

function appOrigin() {
  const ORIGIN = {
    PROD: "https://app.treasury.todaq.net",
    STAGING: "https://treasury-staging.todaq.net",
  };

  let origin = ORIGIN[info.env];
  if (origin) {
    return origin;
  }

  if (info.env === "DEV") {
    return window.location.origin;
  }
}

async function oidcDiscovery({ issuer, hostname, client_id }) {
  let options = {};
  if (info.env === "DEV") {
    if (issuer.host.includes("localhost") || !!issuer.host.split(":")[1]) {
        options.execute = [ OIDC.allowInsecureRequests];
        options[OIDC.customFetch] = (url, fetchOptions) => {
            url = new URL(url);
            url.searchParams.append("todaTwinHost", hostname || client_id);
            return globalThis.fetch(url, fetchOptions);
        };
    }
  }

  return OIDC.discovery(issuer, client_id, {}, undefined, options);
}

async function setPKCE(key = "pkce") {
  let code_verifier = OIDC.randomPKCECodeVerifier();
  let code_challenge = await OIDC.calculatePKCECodeChallenge(code_verifier);
  let pkce = {
    code_verifier,
    code_challenge,
    code_challenge_method: "S256"
  }

  localStorage.setItem(key, JSON.stringify(pkce));

  return pkce;
}

function getPKCE(key = "pkce") {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (error) {
    return;
  }
}

function rmPKCE(key = "pkce") {
  localStorage.clear(key);
}

function setAuthNonce(key = "auth-nonce") {
  let state = OIDC.randomNonce();
  localStorage.setItem(key, state);
  return state;
}
function getAuthNonce(key = "auth-nonce") {
  return localStorage.getItem(key);
}
function validateAuthNonce(nonce, key = "auth-nonce") {
  if(nonce !== getAuthNonce(key)) {
    return new Error("Nonce mismatch!")
  }
}

export function SignIn() {
  const navigate = useNavigate();
  let { setActiveProfile, updateProfile, getProfile } = useProfiles();
  // useTreasuryClient
  async function submitAuthKickoff(e) {
    e.preventDefault();
    let formData = new FormData(e.target);
    let twinUrl = formData.get("twin-url");
    let issuer = new URL(`${twinUrl}/oauth`);
    if (info.env === "DEV") {
      let localIssuerPort = formData.get("local-iss-port");
      issuer = new URL(`http://localhost:${localIssuerPort}/oauth`);
    }

    let authConfig = await oidcDiscovery({
      issuer,
      client_id: config.authClientId,
      hostname: twinHostFromUrl(twinUrl),
    });

    let { code_challenge, code_challenge_method } = await setPKCE();

    let state = setAuthNonce();
    let authUrl = OIDC.buildAuthorizationUrl(authConfig, {
      redirect_uri: `${appOrigin()}/signin`,
      scope: "transfer",
      code_challenge,
      code_challenge_method,
      state
    });

    // NOTE(sfertman): todaTwinHost query param is needed in
    // DEV because everything is running on localhost
    if(info.env === "DEV") {
      authUrl.searchParams.append("todaTwinHost", twinHostFromUrl(twinUrl))
    }

    window.location.href = authUrl;
  }

  const [searchParams, _] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const nonceError = validateAuthNonce(state);

  // FIXME(sfertman): cannot remove nonce because React rerenders stuff
  // all the time and then it cannot find the once valid nonce that's
  // been long removed. Find a better way to manage nonce state here.
  // if (!nonceError) {
  //   rmAuthNonce();
  // }
  const issuer = searchParams.get("iss");
  const twinHost = twinHostFromUrl(issuer) || info.env === "DEV" && searchParams.get("todaTwinHost");

  const twinUrl = `https://${twinHost}`;
  const token = getProfile(twinUrl)?.client?.token;
  if (token) {
    navigate("/home");
  }
  const { data: accessToken, isLoading: isLoadingToken, error: tokenError } = useQuery({
    queryKey: ["OIDC", code, state, issuer],
    enabled: !!issuer && !!code && !!state && !token,

    queryFn: async function accessTokenQuery() {
      let authConfig = await oidcDiscovery({
        issuer: new URL(issuer),
        client_id: config.authClientId,
        hostname: twinHost,
      });
      let token = await OIDC.authorizationCodeGrant(authConfig, new URL(window.location.href), {
        pkceCodeVerifier: getPKCE()?.code_verifier,
        expectedState: state
      });

      updateProfile(twinUrl, cfg => {

        if (!cfg) {
          cfg = { client: { url: twinUrl, token } }
        } else {
          cfg.client.token = token
        }
        return cfg;
      });

      setActiveProfile(twinUrl);

      rmPKCE();

      return token;
    }
  })
  useEffect(() => {
    if (accessToken) {
      navigate("/home")
    }
  }, [accessToken])

  return <Page>
    <div className="flex flex-col ~bg-slate-400 grow h-full justify-items-center align-middle">
      {searchParams.size > 0 &&
        <div>
          <pre>{JSON.stringify(Object.fromEntries(searchParams.entries()), null, 2)}</pre>
          {nonceError ?
            <div className="w-full border border-error rounded-md text-error text-center p-2 py-4">
              Authentication failed: {nonceError.message}
            </div> :
            isLoadingToken ?
              "Getting token..." :
              tokenError
                && `Error getting token: ${tokenError}\n${tokenError.stack}`}
        </div>}
      <div className="w-full ~bg-green-300 mt-36">
        <p className="text-2xl text-center text-toda-gray p-10">Sign In</p>
        <form
          onSubmit={submitAuthKickoff}
          className="w-full flex flex-col gap-4" >
          <label className="gap-2 flex flex-col text-on-neutral-bg-600 text-sm font-medium">
            <p className="font-medium text-sm text-neutral-600">Twin URL</p>
            <input
              // TODO: input url validation
              id="twin-url"
              name="twin-url"
              type="url"
              required
              autoComplete="true"
              defaultValue={searchParams.get("twinUrl")}
              className="px-2 py-1 border rounded" />
          </label>
          {info.env === "DEV" &&
          <label className="gap-2 flex flex-col text-on-neutral-bg-600 text-sm font-medium">
            <p className="font-medium text-sm text-neutral-600">Local DEV authorizer port</p>
            <input
              name="local-iss-port"
              type="number"
              placeholder="e.g. 8000"
              className="px-2 py-1 border rounded" />
          </label>}
          <ButtonOk type="submit" className="w-full">
            Next
          </ButtonOk>
        </form>
      </div>
    </div>
  </Page>;
}
